<template>
  <div v-if="pluginLoaded" id="pluginDetails">
    <c-row>
      <c-col lg="4" md="12" class="wp-info">
        <template v-if="pluginDataNotFound">
          <c-card>
            <c-card-body>
              <c-row>
                <c-col sm="2">
                  <img width="64px" :src="placeholderImage" class="" />
                </c-col>
                <c-col sm="10">
                  <h1 class="h4 mb-0 mt-2"><strong>Plugin summary</strong></h1>
                  <p class="lead mt-2">
                    We have no plugin data to show yet. KoalaBoss is current learning about this new plugin and will
                    have something ready for you shortly.
                  </p>
                </c-col>
              </c-row>
            </c-card-body>
          </c-card>
        </template>
        <template v-else>
          <c-card>
            <c-card-body>
              <c-row>
                <c-col sm="2">
                  <img v-lazy="getPluginThumbUrl()" width="64px" class="" />
                </c-col>
                <c-col sm="10">
                  <h1 class="h4 mb-0 mt-2">
                    <strong>{{ plugin.name }}</strong>
                  </h1>
                  <small v-if="plugin.wpData" v-html="plugin.wpData.author" />
                </c-col>
              </c-row>

              <template v-if="plugin.wpData">
                <c-row class="mt-3">
                  <c-col sm="6">
                    <strong>Version</strong>
                  </c-col>
                  <c-col sm="6">
                    {{ plugin.wpData.version }}
                  </c-col>

                  <c-col sm="6">
                    <strong>Last updated</strong>
                  </c-col>
                  <c-col sm="6">
                    {{ lastUpdatedDisplay | dateFormatRelative }}
                  </c-col>

                  <c-col sm="6">
                    <strong>Downloaded</strong>
                  </c-col>
                  <c-col sm="6">
                    {{ downloadCount }}
                  </c-col>

                  <c-col sm="6">
                    <strong>WordPress version</strong>
                  </c-col>
                  <c-col sm="6">{{ plugin.wpData.requires }} or higher</c-col>

                  <c-col sm="6">
                    <strong>Tested up to</strong>
                  </c-col>
                  <c-col sm="6">
                    {{ plugin.wpData.tested }}
                  </c-col>

                  <c-col sm="6">
                    <strong>Rating</strong>
                  </c-col>
                  <c-col sm="6">{{ plugin.wpData.rating }}%</c-col>
                </c-row>

                <c-row class="mt-3 wp-descriptions">
                  <c-col>
                    <p class="lead section-header font-weight-bold mb-2">Description</p>
                    <p class="info" v-html="plugin.wpData.sections.description" />
                    <p class="lead section-header font-weight-bold mb-2">Change Log</p>
                    <p class="info" v-html="plugin.wpData.sections.changelog" />
                    <p class="lead section-header font-weight-bold mb-2">FAQ</p>
                    <p class="info" v-html="plugin.wpData.sections.faq" />
                    <p class="lead section-header font-weight-bold mb-2">Installation</p>
                    <p class="info" v-html="plugin.wpData.sections.installation" />
                  </c-col>
                </c-row>
              </template>
            </c-card-body>
          </c-card>
        </template>
      </c-col>
      <c-col lg="8" md="12">
        <c-tabs class="">
          <c-tab title="Sites" active>
            <template slot="title">
              <site-icon />
              Sites with this plugin
              <pill :value="sites.length" />
            </template>
            <site-list
              :sites="sites"
              :plugin="plugin"
              :show-only-require-updates="false"
              @updateAll="showUpdateAllPluginsModal = true"
              @updateSingle="updateSingle"
            />
          </c-tab>
        </c-tabs>
      </c-col>
    </c-row>
    <confirm-modal title="Update All Plugins" :show.sync="showUpdateAllPluginsModal" @update:confirm="updateAll">
      <div v-html="updateAllPluginsMessage" />
    </confirm-modal>
  </div>
</template>

<script>
import Vue from 'vue';
import VueLazyload from 'vue-lazyload';
import { freeSet } from '@coreui/icons';
import _ from 'lodash';
import io from 'socket.io-client';
import SiteList from '../components/site-list-for-plugin';
import Pill from '../components/Pill';
import { buildEndpoint } from '@/libs/buildEndPoint';
import SiteIcon from '../components/icons/site';
import ConfirmModal from '@/views/components/ConfirmModal';
import { errorMessage, successMessage } from '@/libs/ui/message';

const socket = io(buildEndpoint(''));

const placeholderImage = process.env.VUE_APP_PLUGIN_PLACEHOLDER_IMAGE_URL;
Vue.use(VueLazyload, {
  preLoad: 1.3,
  error: placeholderImage,
  loading: placeholderImage,
  attempt: 1,
});

export default {
  name: 'PluginDetails',
  components: {
    SiteList,
    Pill,
    SiteIcon,
    ConfirmModal,
  },

  data() {
    return {
      showUpdateAllPluginsModal: false,
      placeholderImage,
      pluginDataNotFound: false,
      pluginLoaded: false,
      plugin: null,
      sites: null,
      freeSet,
      successfulSiteUpdates: [],
      updateCount: 0,
    };
  },

  computed: {
    updateAllPluginsMessage() {
      return `Update <strong>${this.plugin.name}</strong>, on ${this.updateCount} site${this.updateCount > 1 ? 's' : ''}, where it has not been blocked?`;
    },
    lastUpdatedDisplay() {
      const lastUpdated = _.get(this.plugin, 'wpData.last_updated');
      if (typeof lastUpdated === 'string') {
        // get date only, this is in an invalid ISO format
        return lastUpdated.substring(0, lastUpdated.indexOf(' '));
      }
      return '';
    },
    downloadCount() {
      const downloads = _.get(this.plugin, 'wpData.downloaded');
      if (downloads > 5000000) {
        return '5m+';
      } if (downloads > 1000000) {
        return '1m+';
      } if (downloads > 500000) {
        return '500k+';
      } if (downloads > 100000) {
        return '100k+';
      } if (downloads > 50000) {
        return '50k+';
      } if (downloads > 10000) {
        return '10k+';
      }
      return downloads;
    },
  },
  mounted() {},

  created() {
    this.pluginKey = this.$route.params.pluginKey;
    this.get();
    socket.emit('room', Vue.localStorage.get('mp-sid'));
    socket.on('pluginUpdateNotification', (data) => {
      if (data.action === 'end-plugin-update' && data.status) {
        // @TODO - Need a way to refresh the site, then redraw the list of plugins
        console.log(`Plugin update success: ${data.siteId}`);
        this.successfulSiteUpdates.push(data.siteId);
      }
    });
    socket.on('siteRefreshNotification', (data) => {
      if (data.action === 'site-refresh-finished') {
        const { siteId } = data;
        console.log(this.successfulSiteUpdates.includes(siteId));
        if (this.successfulSiteUpdates.includes(siteId)) {
          // Set site plugin status updates
          console.log('Set site updated');
          const site = this.sites.find((site) => site._id === siteId);
          site.plugin.Version = `${site.plugin.updateInfo.newVersion} (updated)`;
          site.plugin.updateFound = false;
        }
      }
    });
  },

  updated() {
    // Force all wp links (from wp info data), to open in a new window.
    $('.wp-info a').prop('target', '_blank');
  },

  methods: {
    getPluginThumbUrl() {
      return this.plugin.images.thumb.url;
    },
    get() {
      this.$http
        .get(buildEndpoint('plugin/sites/') + encodeURIComponent(this.pluginKey))
        .then((response) => {
          this.error = null;
          if (response.data.success) {
            const plugin = _.get(response, 'data.data.plugin');
            if (Object.keys(plugin).length === 0) {
              this.pluginDataNotFound = true;
            }
            this.plugin = plugin;
            this.sites = _.get(response, 'data.data.sites');
            this.sites.forEach((site) => {
              if (site.plugin.updateFound) {
                this.updateCount += 1;
              }
            });
            this.pluginLoaded = true;
          } else {
            this.error = 'Error getting plugin data';
          }
        })
        .catch((error) => {
          this.error = error;
        });
    },
    updateSingle(plugin) {
      console.log(plugin);
    },
    updateAll() {
      this.$http
        .get(buildEndpoint('plugin/sites/update/') + encodeURIComponent(this.pluginKey))
        .then((response) => {
          this.showUpdateAllPluginsModal = false;
          if (response.data.success) {
            this.$store.commit('openSideBar');
          } else {
            errorMessage(this.$store, { message: response.data.error || 'There was a problem updating this plugin' });
          }
        })
        .catch((error) => {
          this.error = error;
        });
    },
  },
};
</script>

<style lang="scss">
.info {
  background: #eee;
  max-height: 200px;
  overflow: auto;
  padding: 10px;
  h3 {
    font-size: 14px;
  }
  h4 {
    font-size: 14px;
  }
}
.c-dark-theme {
  .info {
    background: rgb(53, 55, 57);
    max-height: 200px;
    overflow: auto;
    padding: 10px;
    h3 {
      font-size: 14px;
    }
    h4 {
      font-size: 14px;
    }
  }
}
</style>
